'use client';

import { AlertDialog } from '@/components/ui/alert-dialog';
import { Drawer } from '@/components/ui/drawer';
import * as React from 'react';
import { useTranslations } from 'next-intl';

import {
  AlertDialogCompactSurface,
  AlertDialogDrawerSurface,
  type AlertDialogSurfaceView,
} from './alert-dialog-surfaces';
import { onWorkspaceAdmissionEnd } from '@/providers/workspace-admission-lease';

type AlertInput =
  | string
  | {
      title?: string;
      description: React.ReactNode;
      okText?: string;
    };

type AlertRequest = {
  title?: string;
  description: React.ReactNode;
  okText: string;
  resolve: () => void;
};

type ConfirmInputObject = {
  title?: string;
  description: React.ReactNode;
  /**
   * Optional rich body content rendered below the description.
   * Use this for inputs, lists, or any non-text content to avoid nesting
   * block elements inside the description paragraph.
   */
  body?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  initialFocus?: 'confirm' | 'cancel';
};

type ConfirmRequest = {
  title?: string;
  description: React.ReactNode;
  confirmText: string;
  cancelText: string;
  resolve: (value: boolean) => void;
  body?: React.ReactNode;
  destructive?: boolean;
  initialFocus?: 'confirm' | 'cancel';
};

type AlertDialogContextValue = {
  alert: (input: AlertInput) => Promise<void>;
  confirm: (input: string | ConfirmInputObject) => Promise<boolean>;
};

const AlertDialogContext = React.createContext<
  AlertDialogContextValue | undefined
>(undefined);

export function AlertDialogProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const t = useTranslations('Common');
  const [queue, setQueue] = React.useState<Array<AlertRequest>>([]);
  const current = queue.length > 0 ? queue[0] : null;
  const [open, setOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(false);
  const [confirmState, setConfirmState] = React.useState<ConfirmRequest | null>(
    null
  );

  React.useEffect(() => {
    setOpen(Boolean(current));
  }, [current]);

  // Callers may put workspace media in a dialog body. This provider sits
  // above the admitted workspace subtree, so when that admission ends
  // (workspace switch, membership loss, sign-out) settle and drop every
  // pending dialog: alerts resolve, confirms resolve as cancelled (#4628).
  React.useEffect(
    () =>
      onWorkspaceAdmissionEnd(() => {
        setOpen(false);
        setQueue((pending) => {
          for (const request of pending) request.resolve();
          return [];
        });
        setConfirmState((pending) => {
          pending?.resolve(false);
          return null;
        });
      }),
    []
  );

  React.useEffect(() => {
    const check = () =>
      setIsMobile(window.matchMedia('(max-width: 640px)').matches);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const closeCurrent = React.useCallback(() => {
    setOpen(false);
    if (current) {
      // Resolve the awaiting promise, then dequeue
      current.resolve();
      setQueue((q) => q.slice(1));
    }
  }, [current]);

  const alert = React.useCallback(
    (input: AlertInput) => {
      return new Promise<void>((resolve) => {
        let req: AlertRequest;
        if (typeof input === 'string') {
          req = { description: input, okText: t('ok'), resolve };
        } else {
          const base = {
            description: input.description,
            okText: input.okText ?? t('ok'),
            resolve,
          } as const;
          req = input.title ? { title: input.title, ...base } : base;
        }
        setQueue((q) => [...q, req]);
      });
    },
    [t]
  );

  const confirm = React.useCallback(
    (input: string | ConfirmInputObject) => {
      return new Promise<boolean>((resolve) => {
        const state: ConfirmRequest =
          typeof input === 'string'
            ? {
                description: input,
                confirmText: t('confirm'),
                cancelText: t('cancel'),
                destructive: false,
                initialFocus: 'cancel',
                resolve,
              }
            : {
                ...(input.title ? { title: input.title } : {}),
                description: input.description,
                confirmText: input.confirmText ?? t('confirm'),
                cancelText: input.cancelText ?? t('cancel'),
                destructive: input.destructive ?? false,
                initialFocus: input.initialFocus ?? 'cancel',
                resolve,
                ...(input.body ? { body: input.body } : {}),
              };
        setConfirmState(state);
      });
    },
    [t]
  );

  const cancelButtonRef = React.useRef<HTMLButtonElement>(null);
  const confirmButtonRef = React.useRef<HTMLButtonElement>(null);
  const focusInitialConfirmAction = React.useCallback(
    (event: Event) => {
      if (!confirmState) return;
      event.preventDefault();
      const target =
        confirmState.initialFocus === 'confirm'
          ? confirmButtonRef.current
          : cancelButtonRef.current;
      target?.focus({ preventScroll: true });
    },
    [confirmState]
  );

  const value = React.useMemo<AlertDialogContextValue>(
    () => ({ alert, confirm }),
    [alert, confirm]
  );

  const view: AlertDialogSurfaceView | null = confirmState
    ? {
        title: confirmState.title || t('confirm'),
        showTitle: Boolean(confirmState.title),
        description: confirmState.description,
        ...(confirmState.body ? { body: confirmState.body } : {}),
        confirmText: confirmState.confirmText,
        cancelText: confirmState.cancelText,
        destructive: confirmState.destructive ?? false,
      }
    : current
      ? {
          title: current.title || t('alert'),
          showTitle: Boolean(current.title),
          description: current.description,
          confirmText: current.okText,
          destructive: false,
        }
      : null;

  const settleConfirm = (value: boolean) => {
    if (!confirmState) return;
    confirmState.resolve(value);
    setConfirmState(null);
  };
  const dismiss = () => {
    if (confirmState) settleConfirm(false);
    else closeCurrent();
  };
  const accept = () => {
    if (confirmState) settleConfirm(true);
    else closeCurrent();
  };
  const surfaceProps = view
    ? {
        view,
        confirmRef: confirmButtonRef,
        cancelRef: cancelButtonRef,
        autoFocusConfirm: confirmState
          ? confirmState.initialFocus === 'confirm'
          : true,
        ...(confirmState ? { onOpenAutoFocus: focusInitialConfirmAction } : {}),
        onConfirm: accept,
        onCancel: dismiss,
      }
    : null;
  const isOpen = open || Boolean(confirmState);
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) dismiss();
  };

  return (
    <AlertDialogContext.Provider value={value}>
      {children}
      {isMobile ? (
        <Drawer open={isOpen} onOpenChange={handleOpenChange}>
          {surfaceProps ? <AlertDialogDrawerSurface {...surfaceProps} /> : null}
        </Drawer>
      ) : (
        <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
          {surfaceProps ? (
            <AlertDialogCompactSurface {...surfaceProps} />
          ) : null}
        </AlertDialog>
      )}
    </AlertDialogContext.Provider>
  );
}

/**
 * Dialogs, fenced to the calling component's mount (see useFullscreenViewer):
 * a late `alert`/`confirm` from a component unmounted with its workspace
 * admission settles at once (alert resolves, confirm resolves false) and
 * never shows the previous workspace's content (#4628).
 */
export function useAlertDialog(): AlertDialogContextValue {
  const ctx = React.useContext(AlertDialogContext);
  if (!ctx) {
    throw new Error('useAlertDialog must be used within AlertDialogProvider');
  }
  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  return React.useMemo(
    () => ({
      alert: (input: AlertInput) =>
        mountedRef.current ? ctx.alert(input) : Promise.resolve(),
      confirm: (input: string | ConfirmInputObject) =>
        mountedRef.current ? ctx.confirm(input) : Promise.resolve(false),
    }),
    [ctx]
  );
}
